import React from "react";
import { Banknote, Building2, Hash, QrCode, Landmark } from "lucide-react";

export interface AccountDetails {
  bankName: string;
  branchName: string;
  accountNumber: string;
  ifsc: string;
  qrCodeKey?: string;
  qrCodeUrl?: string;
}

interface AccountDetailsCardProps {
  accountDetails: AccountDetails | null | undefined;
}

export const AccountDetailsCard = ({ accountDetails }: AccountDetailsCardProps) => {
  if (!accountDetails) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-gray-500 italic">
        Account details not available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-4 flex items-center gap-3">
        <div className="p-2 bg-white/20 rounded-lg">
          <Banknote className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-lg font-bold">Bank Account Details</h3>
          <p className="text-sm text-white/90">
            Please transfer the application fee to this account
          </p>
        </div>
      </div>

      {/* Account Info Card */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-5 border border-blue-100">
        {/* Bank Name */}
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm text-gray-600">Bank Name</p>
            <p className="text-lg font-semibold text-gray-900">
              {accountDetails.bankName}
            </p>
          </div>
        </div>

        {/* Branch Name */}
        <div className="flex items-center gap-3">
          <Landmark className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm text-gray-600">Branch Name</p>
            <p className="text-lg font-semibold text-gray-900">
              {accountDetails.branchName}
            </p>
          </div>
        </div>

        {/* Account Number */}
        <div className="flex items-center gap-3">
          <Hash className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm text-gray-600">Account Number</p>
            <p className="text-lg font-semibold text-gray-900">
              {accountDetails.accountNumber}
            </p>
          </div>
        </div>

        {/* IFSC Code */}
        <div className="flex items-center gap-3">
          <Banknote className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm text-gray-600">IFSC Code</p>
            <p className="text-lg font-semibold text-gray-900">
              {accountDetails.ifsc}
            </p>
          </div>
        </div>

        {/* QR Code Image */}
        {accountDetails.qrCodeKey && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600 mb-2 flex items-center justify-center gap-2">
              <QrCode className="w-5 h-5 text-blue-600" />
              Scan QR to Pay
            </p>
            <img
              src={accountDetails.qrCodeUrl}
              alt="QR Code"
              className="w-48 h-48 object-contain mx-auto border-2 border-blue-100 rounded-lg shadow-md"
            />
          </div>
        )}
      </div>
    </div>
  );
};
